import logoImage from "@/assets/images/logo.png"
import logoImageDark from "@/assets/images/logo-dark.png"
import { RegisterForm } from "@/components/auth/register-form"
import { Alert, AlertDescription } from "@/components/ui/alert"
import InvitationService from "@/services/invitation-service"
import { CheckCircle } from "lucide-react"
import * as React from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { getReturnUrl } from "@/utils/return-url"
import { withInvitationId } from "@/utils/invitation-url"

/**
 * Signup page component
 * Provides registration form and social authentication options
 * Handles invitation flows for new users who need to create accounts
 */
export function SignupPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [invitationMessage, setInvitationMessage] = React.useState<string | null>(null)
  const [prefilledEmail, setPrefilledEmail] = React.useState<string>("")
  const [isFromInvitation, setIsFromInvitation] = React.useState<boolean>(false)
  const [invitationId, setInvitationId] = React.useState<string | null>(null)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)

  // Fetch invitation details when invitation ID is available
  const fetchInvitationDetails = React.useCallback(async (invitationIdParam: string) => {
    try {
      setIsLoading(true)
      setError(null)

      const details = await InvitationService.getInvitationDetails(invitationIdParam)
      setPrefilledEmail(details.email) // Set prefilled email from invitation details

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load invitation details'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Check if coming from invitation flow
  React.useEffect(() => {
    // Check URL parameters first
    const invitationIdParam = searchParams.get('invitationId')
    if (invitationIdParam) {
      setInvitationId(invitationIdParam)
      setIsFromInvitation(true)
      // Fetch invitation details to get the email
      fetchInvitationDetails(invitationIdParam)
    }

    // Then check location state
    const state = location.state as any
    if (state?.fromInvitation) {
      setIsFromInvitation(true)
      if (state?.invitationId) {
        setInvitationId(state.invitationId)
        // Fetch invitation details to get the email
        fetchInvitationDetails(state.invitationId)
      }
      if (state?.message) {
        setInvitationMessage(state.message)
      }
    }
    // Remove the direct email setting from state since we'll fetch it from invitation details
    // if (state?.email) {
    //   setPrefilledEmail(state.email)
    // }
  }, [location.state, searchParams, fetchInvitationDetails])


  const handleRegisterSuccess = async () => {
    // If from invitation, redirect to workspace app directly (skip workspace creation)
    // Invitation was already accepted in the invitation page
    if (isFromInvitation && invitationId) {
      navigate(withInvitationId('/app', invitationId), { replace: true })
    } else {
      // Normal signup flow — honour a deep-link destination if the user got
      // here from one (e.g. a channel's "link your account" card), else home.
      navigate(getReturnUrl(searchParams) ?? "/", { replace: true })
    }
  }

  // Show loading state while fetching invitation details
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Background decoration - same as home page */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 lg:w-96 lg:h-96 bg-primary-100 dark:bg-primary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
          <div className="absolute top-40 right-10 w-72 h-72 lg:w-96 lg:h-96 bg-secondary-100 dark:bg-secondary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
          <div className="absolute -bottom-8 left-20 w-72 h-72 lg:w-96 lg:h-96 bg-primary-50 dark:bg-primary-950/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
        </div>

        <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-md">
            <div className="bg-card shadow-xl rounded-2xl border border-border p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading invitation details...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background decoration - same as home page */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 lg:w-96 lg:h-96 bg-primary-100 dark:bg-primary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
        <div className="absolute top-40 right-10 w-72 h-72 lg:w-96 lg:h-96 bg-secondary-100 dark:bg-secondary-900/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
        <div className="absolute -bottom-8 left-20 w-72 h-72 lg:w-96 lg:h-96 bg-primary-50 dark:bg-primary-950/30 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-xl opacity-70" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        {/* Registration Card - just the card, nothing else */}
        <div className="w-full max-w-md">
          <div className="bg-card shadow-xl rounded-2xl border border-border p-8">
            {/* Logo inside the card */}
            <div className="flex justify-center mb-6">
              <img
                src={logoImage}
                alt="Taqat AI Logo"
                className="h-10 w-auto block dark:hidden"
              />
              <img
                src={logoImageDark}
                alt="Taqat AI Logo"
                className="h-10 w-auto hidden dark:block"
              />
            </div>

            {/* Show error if invitation details failed to load */}
            {error && (
              <Alert className="border-red-200 bg-red-50 mb-6">
                <AlertDescription className="text-red-700">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {/* Show invitation message */}
            {invitationMessage && (
              <Alert className="border-green-200 bg-green-50 mb-6">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">
                  {invitationMessage}
                  <p className="text-sm mt-2">Complete your account setup to join the workspace.</p>
                </AlertDescription>
              </Alert>
            )}

            <RegisterForm
              onSuccess={handleRegisterSuccess}
              prefilledEmail={prefilledEmail}
            />

            {/* Divider - Hidden until social login is implemented */}
            {/* <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-neutral-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-3 text-neutral-500">
                  Or continue with
                </span>
              </div>
            </div> */}

            {/* Social Login - Hidden until OAuth is implemented */}
            {/* <SocialLogin /> */}
          </div>
        </div>
      </div>
    </div>
  )
}
